/* eslint-disable react/prop-types */
"use client";

import React from "react";
import { useTranslation } from "react-i18next";
import api from "../../api/axios";
import AppModalShell, { AppModalButton } from "../common/AppModalShell";
import { ParticipantCard } from "../menu/ChatParticipants";
import EventToolsBackButton from "./EventToolsBackButton";
import EventToolsSkeleton, {
  EventToolsAnimatedState,
} from "./EventToolsSkeletons";

export const PARTICIPANT_REQUEST_RESPONSE_MAX_LENGTH = 500;

export const ParticipantRequestDeclineModal = ({
  visible = false,
  requestItem = null,
  submitting = false,
  onClose = () => {},
  onSubmit = () => {},
}) => {
  const { t } = useTranslation("events");
  const [responseMessage, setResponseMessage] = React.useState("");
  const user = requestItem?.user || {};
  const participantLabel = String(user?.code || user?.username || "").trim();
  const username = String(user?.username || "").trim();
  const trimmedResponseMessage = responseMessage.trim();

  React.useEffect(() => {
    if (!visible) setResponseMessage("");
  }, [visible]);

  const safeClose = () => {
    if (!submitting) onClose();
  };

  const footer = (
    <div className="space-y-3">
      <AppModalButton
        variant="danger"
        onClick={() => onSubmit(trimmedResponseMessage)}
        disabled={!trimmedResponseMessage || submitting}
        loading={submitting && Boolean(trimmedResponseMessage)}
      >
        {submitting
          ? t("events.details.more_actions.review_requests_modal.reject_modal.sending")
          : t("events.details.more_actions.review_requests_modal.reject_modal.send")}
      </AppModalButton>
      <AppModalButton
        variant="secondary"
        onClick={() => onSubmit("")}
        disabled={submitting}
      >
        {t(
          "events.details.more_actions.review_requests_modal.reject_modal.without_personal_message"
        )}
      </AppModalButton>
    </div>
  );

  return (
    <AppModalShell
      open={visible}
      title={t(
        "events.details.more_actions.review_requests_modal.reject_modal.title",
        {
          participant:
            participantLabel ||
            t(
              "events.details.more_actions.review_requests_modal.reject_modal.participant_fallback"
            ),
        }
      )}
      onClose={safeClose}
      closeAriaLabel={t(
        "events.details.more_actions.review_requests_modal.reject_modal.close"
      )}
      closeOnBackdropClick={!submitting}
      zIndexClassName="z-[14000]"
      maxWidthClassName="max-w-[520px]"
      footer={footer}
    >
      <div className="space-y-5">
        <p className="text-[14px] font-medium leading-6 text-white/82">
          {t(
            "events.details.more_actions.review_requests_modal.reject_modal.explanation"
          )}
        </p>

        {participantLabel ? (
          <div className="rounded-[18px] border border-white/12 bg-white/[0.07] px-4 py-3">
            <div className="text-[16px] font-extrabold leading-5 text-white">
              {participantLabel}
            </div>
            {username ? (
              <div className="mt-1 truncate text-[13px] font-medium text-white/64">
                @{username}
              </div>
            ) : null}
          </div>
        ) : null}

        <div>
          <label
            htmlFor="participant-request-response-message"
            className="block text-sm font-semibold text-white/90"
          >
            {t(
              "events.details.more_actions.review_requests_modal.reject_modal.response_label"
            )}
          </label>
          <textarea
            id="participant-request-response-message"
            value={responseMessage}
            onChange={(event) =>
              setResponseMessage(
                event.target.value.slice(0, PARTICIPANT_REQUEST_RESPONSE_MAX_LENGTH)
              )
            }
            maxLength={PARTICIPANT_REQUEST_RESPONSE_MAX_LENGTH}
            disabled={submitting}
            placeholder={t(
              "events.details.more_actions.review_requests_modal.reject_modal.response_placeholder"
            )}
            className="mt-2 min-h-[118px] w-full resize-none rounded-[18px] border border-white/12 bg-white/10 px-4 py-3 text-sm font-medium leading-5 text-white outline-none placeholder:text-white/42 disabled:opacity-55"
          />
          <div className="mt-2 flex items-start justify-between gap-4 text-[12px] font-medium leading-5 text-white/58">
            <span>
              {t(
                "events.details.more_actions.review_requests_modal.reject_modal.response_hint"
              )}
            </span>
            <span className="shrink-0 tabular-nums">
              {responseMessage.length}/{PARTICIPANT_REQUEST_RESPONSE_MAX_LENGTH}
            </span>
          </div>
        </div>
      </div>
    </AppModalShell>
  );
};

const ParticipantRequestsReviewModal = ({
  visible = false,
  eventId = null,
  requestStatusFilter = "pending",
  onClose = () => {},
  onUpdated = null,
  onViewed = null,
  onOpenProfile = null,
  inline = false,
  onBack = null,
  embedded = false,
  hasRequestForm = false,
}) => {
  const { t } = useTranslation("events");

  const [loading, setLoading] = React.useState(false);
  const [requestsLoaded, setRequestsLoaded] = React.useState(false);
  const [processingRequestId, setProcessingRequestId] = React.useState(null);
  const [requests, setRequests] = React.useState([]);
  const [rejectTarget, setRejectTarget] = React.useState(null);
  const requestLoadSequenceRef = React.useRef(0);
  const onViewedRef = React.useRef(onViewed);
  const onUpdatedRef = React.useRef(onUpdated);
  const translationRef = React.useRef(t);

  React.useEffect(() => {
    onViewedRef.current = onViewed;
  }, [onViewed]);

  React.useEffect(() => {
    onUpdatedRef.current = onUpdated;
  }, [onUpdated]);

  React.useEffect(() => {
    translationRef.current = t;
  }, [t]);

  const loadRequests = React.useCallback(async () => {
    const requestSequence = requestLoadSequenceRef.current + 1;
    requestLoadSequenceRef.current = requestSequence;
    if (!eventId) {
      setRequestsLoaded(true);
      return;
    }
    setLoading(true);
    setRequestsLoaded(false);
    try {
      const { data } = await api.get(`/events/${eventId}/host-participant-requests/`, {
        params: { status: requestStatusFilter, mark_host_seen: 1 },
      });
      if (requestLoadSequenceRef.current !== requestSequence) return;
      setRequests(Array.isArray(data?.requests) ? data.requests : []);
      if (typeof onViewedRef.current === "function") {
        Promise.resolve(onViewedRef.current(data || null)).catch(() => {});
      }
    } catch (err) {
      if (requestLoadSequenceRef.current !== requestSequence) return;
      const status = err?.response?.status;
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        translationRef.current("events.details.generic_error");
      window.dispatchEvent(
        new CustomEvent("global-error", { detail: { message: msg, status } })
      );
    } finally {
      if (requestLoadSequenceRef.current !== requestSequence) return;
      setLoading(false);
      setRequestsLoaded(true);
    }
  }, [eventId, requestStatusFilter]);

  React.useEffect(() => {
    if (!visible || !eventId) return;
    void loadRequests();
  }, [eventId, loadRequests, visible]);

  React.useEffect(() => {
    if (visible) return;
    requestLoadSequenceRef.current += 1;
    setLoading(false);
    setRequestsLoaded(false);
    setProcessingRequestId(null);
    setRequests([]);
    setRejectTarget(null);
  }, [visible]);

  const handleDecision = async (
    requestId,
    decision = "approve",
    responseMessage = ""
  ) => {
    if (!eventId || !requestId || processingRequestId) return false;
    setProcessingRequestId(`${requestId}:${decision}`);
    try {
      const payload = {
        request_id: requestId,
        decision,
      };
      if (decision === "reject") {
        payload.response_message = String(responseMessage || "").trim();
      }
      const { data } = await api.post(
        `/events/${eventId}/host-participant-requests/`,
        payload
      );

      setRequests((prev) =>
        requestStatusFilter === "all"
          ? prev.map((item) =>
              item.request_id === requestId
                ? {
                    ...item,
                    status:
                      data?.status ||
                      (decision === "reject" ? "rejected" : "approved"),
                    response_message: data?.response_message || "",
                  }
                : item
            )
          : prev.filter((item) => item.request_id !== requestId)
      );
      window.dispatchEvent(
        new CustomEvent("global-info", {
          detail: {
            message:
              data?.message ||
              t(
                decision === "reject"
                  ? "events.details.more_actions.review_requests_modal.rejected_success"
                  : data?.approval_destination === "waitlist"
                  ? "events.details.more_actions.review_requests_modal.approved_to_waitlist_success"
                  : "events.details.more_actions.review_requests_modal.approved_success"
              ),
          },
        })
      );

      if (typeof onUpdatedRef.current === "function") {
        await onUpdatedRef.current(data || null);
      }
      return true;
    } catch (err) {
      const status = err?.response?.status;
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        t("events.details.generic_error");
      window.dispatchEvent(
        new CustomEvent("global-error", { detail: { message: msg, status } })
      );
      return false;
    } finally {
      setProcessingRequestId(null);
    }
  };

  const submitRejection = async (responseMessage = "") => {
    const requestId = rejectTarget?.request_id;
    if (!requestId) return;
    const succeeded = await handleDecision(requestId, "reject", responseMessage);
    if (succeeded) setRejectTarget(null);
  };

  if (!visible) return null;

  const modalFooter = (
    <AppModalButton
      variant="secondary"
      onClick={onClose}
      disabled={Boolean(processingRequestId)}
    >
      {t("events.details.more_actions.review_requests_modal.close")}
    </AppModalButton>
  );

  const getProcessingLabel = (reqId, decision) => `${reqId}:${decision}`;
  const renderDecisionActions = ({
    requestItem,
    canApprove,
    approvalDestination,
    isApprovingThis,
    isRejectingThis,
  }) => {
    const reqId = requestItem?.request_id;
    return (
      <div
        className="relative z-10 mt-3 grid grid-cols-2 gap-2"
        data-testid={`participant-request-actions-${reqId}`}
      >
        <AppModalButton
          variant="secondary"
          disabled={Boolean(processingRequestId)}
          onClick={() => setRejectTarget(requestItem)}
          loading={isRejectingThis}
          initial={false}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          className="min-w-0 px-3 text-[14px]"
          aria-label={t(
            "events.details.more_actions.review_requests_modal.reject"
          )}
          data-testid={`participant-request-reject-${reqId}`}
        >
          {isRejectingThis
            ? t("events.details.more_actions.review_requests_modal.rejecting")
            : t("events.details.more_actions.review_requests_modal.reject")}
        </AppModalButton>
        <AppModalButton
          disabled={Boolean(processingRequestId) || !canApprove}
          onClick={() => {
            if (!canApprove) return;
            handleDecision(reqId, "approve");
          }}
          loading={isApprovingThis}
          initial={false}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          className="min-w-0 px-3 text-[14px] text-[#2563EB]"
          aria-label={t(
            approvalDestination === "waitlist"
              ? "events.details.more_actions.review_requests_modal.approve_to_waitlist"
              : "events.details.more_actions.review_requests_modal.approve"
          )}
          data-testid={`participant-request-approve-${reqId}`}
        >
          {isApprovingThis
            ? t("events.details.more_actions.review_requests_modal.approving")
            : t(
                approvalDestination === "waitlist"
                  ? "events.details.more_actions.review_requests_modal.approve_to_waitlist"
                  : "events.details.more_actions.review_requests_modal.approve"
              )}
        </AppModalButton>
      </div>
    );
  };

  const renderAnswersBlock = (reqId, qa, className = "") => (
    <div
      className={`relative z-0 mt-3 space-y-2 rounded-[18px] border border-white/10 bg-white/[0.07] p-3 ${className}`}
      data-testid={`participant-request-answers-${reqId}`}
    >
      {qa.length > 0 ? (
        qa.map((entry, qaIndex) => (
          <div key={`${reqId}-qa-${qaIndex}`} className="text-sm leading-5">
            <span className="font-semibold">
              {entry?.question ||
                t("events.details.more_actions.review_requests_modal.question_fallback")}
              :
            </span>{" "}
            <span className="text-white/90">
              {String(
                entry?.answer ||
                  t("events.details.more_actions.review_requests_modal.answer_empty")
              )}
            </span>
          </div>
        ))
      ) : (
        <div className="text-sm text-white/90">
          {t("events.details.more_actions.review_requests_modal.no_answers")}
        </div>
      )}
    </div>
  );

  const skillLevelLabel = (level, fallback = "") => {
    const normalized = Number(level);
    const key = { 1: "beginner", 2: "intermediate", 3: "advanced" }[normalized];
    if (!key) return fallback || t("events.details.more_actions.review_requests_modal.skill.not_calibrated", {
      defaultValue: "Not calibrated",
    });
    return t(`event_create.audience_filters.skill_levels.${key}`, {
      defaultValue: fallback || key.charAt(0).toUpperCase() + key.slice(1),
    });
  };

  const renderSkillReviewBlock = (reqId, skillReview) => {
    const submitted = skillReview?.submitted || null;
    if (!submitted) return null;
    const reason = String(submitted?.reason || "");
    const requiredLabel = skillLevelLabel(
      submitted?.required_level,
      submitted?.required_level_label
    );
    const calibratedLabel = skillLevelLabel(
      submitted?.calibrated_level,
      submitted?.calibrated_level_label
    );
    const selfConfirmedLabel = skillLevelLabel(
      submitted?.self_confirmed_level,
      submitted?.self_confirmed_level_label
    );
    const isMismatch = reason === "skill_level_mismatch";

    return (
      <div
        className="relative z-0 mt-3 rounded-[18px] border border-amber-200/20 bg-amber-300/[0.09] p-3 text-sm leading-5"
        data-testid={`participant-request-skill-${reqId}`}
      >
        <div className="font-semibold text-amber-50">
          {t("events.details.more_actions.review_requests_modal.skill.title", {
            defaultValue: "Skill review",
          })}
        </div>
        <div className="mt-1 text-white/88">
          {t("events.details.more_actions.review_requests_modal.skill.event_level", {
            defaultValue: "Event level: {{level}}",
            level: requiredLabel,
          })}
        </div>
        <div className="text-white/88">
          {isMismatch
            ? t("events.details.more_actions.review_requests_modal.skill.calibrated_level", {
                defaultValue: "Calibrated skill level: {{level}}",
                level: calibratedLabel,
              })
            : t("events.details.more_actions.review_requests_modal.skill.self_confirmed", {
                defaultValue: "Not calibrated · participant selected {{level}} for this request",
                level: selfConfirmedLabel,
              })}
        </div>
        <div className="mt-1 text-[12px] text-amber-50/70">
          {t("events.details.more_actions.review_requests_modal.skill.manual_decision", {
            defaultValue: "Manual host decision required. Approval applies only to this event.",
          })}
        </div>
      </div>
    );
  };

  const renderRequestItem = (requestItem, index, total, answersClassName = "") => {
    const reqId = requestItem?.request_id;
    const user = requestItem?.user || {};
    const qa = Array.isArray(requestItem?.qa) ? requestItem.qa : [];
    const isApprovingThis = processingRequestId === getProcessingLabel(reqId, "approve");
    const isRejectingThis = processingRequestId === getProcessingLabel(reqId, "reject");
    const canApprove = requestItem?.can_approve !== false;
    const approvalDestination =
      requestItem?.approval_destination === "waitlist" ? "waitlist" : "participants";
    const approveBlockMessage = String(requestItem?.approve_block?.error || "").trim();
    const showAnswers = Boolean(hasRequestForm || qa.length > 0);
    const skillReview = requestItem?.skill_review || null;
    const requestStatus = String(requestItem?.status || "pending").toLowerCase();
    const isPending = requestStatus === "pending";
    const openRequestProfile = (u) => {
      if (typeof onOpenProfile !== "function") return;
      const code = u?.code || user?.code || "";
      if (code) onOpenProfile(code, requestStatus);
    };

    return (
      <article
        key={reqId || `request-${index}`}
        className="pb-1"
        data-testid={`participant-request-card-${reqId}`}
      >
        <ParticipantCard
          user={user}
          canKick={false}
          onOpenAvatarProfile={typeof onOpenProfile === "function" ? openRequestProfile : undefined}
          onOpenProfile={openRequestProfile}
        />

        {renderSkillReviewBlock(reqId, skillReview)}

        {!canApprove && approveBlockMessage ? (
          <div className="mt-3 rounded-xl bg-amber-400/15 px-3 py-2 text-sm text-amber-100">
            {approveBlockMessage}
          </div>
        ) : null}

        {showAnswers ? renderAnswersBlock(reqId, qa, answersClassName) : null}

        {isPending ? (
          renderDecisionActions({
            requestItem,
            canApprove,
            approvalDestination,
            isApprovingThis,
            isRejectingThis,
          })
        ) : (
          <div
            className={`mt-3 rounded-full border px-4 py-2 text-center text-[13px] font-semibold ${
              requestStatus === "approved"
                ? "border-emerald-300/25 bg-emerald-400/12 text-emerald-100"
                : "border-rose-300/25 bg-rose-400/12 text-rose-100"
            }`}
          >
            {t(
              requestStatus === "approved"
                ? "events.details.more_actions.review_requests_modal.status_approved"
                : "events.details.more_actions.review_requests_modal.status_rejected"
            )}
          </div>
        )}

        {index < total - 1 ? (
          <div className="mt-4 h-px bg-white/35" />
        ) : null}
      </article>
    );
  };

  const modalBody = requests.length === 0 ? (
    <p className="text-center text-sm text-white/90">
      {t("events.details.more_actions.review_requests_modal.empty")}
    </p>
  ) : (
    <div className="space-y-4">
      {requests.map((requestItem, index) =>
        renderRequestItem(requestItem, index, requests.length)
      )}
    </div>
  );

  const rejectionModal = (
    <ParticipantRequestDeclineModal
      visible={Boolean(rejectTarget)}
      requestItem={rejectTarget}
      submitting={Boolean(processingRequestId)}
      onClose={() => setRejectTarget(null)}
      onSubmit={submitRejection}
    />
  );

  if (embedded) {
    return (
      <>
        <EventToolsAnimatedState
          loading={loading || !requestsLoaded}
          skeleton={<EventToolsSkeleton variant="requests" />}
        >
          {modalBody}
          <div className="mt-5">{modalFooter}</div>
        </EventToolsAnimatedState>
        {rejectionModal}
      </>
    );
  }

  const shellClass = inline
    ? "absolute inset-0 z-[95] flex items-center justify-center"
    : "fixed inset-0 z-[1200] flex items-center justify-center";

  const panelWidthClass = inline ? "w-[94%]" : "w-[94vw]";

  const content = (
    inline ? (
      <div className={shellClass}>
        <div
          className={`absolute inset-0 bg-black/60 ${inline ? "rounded-2xl" : ""}`}
          onClick={onClose}
          aria-hidden="true"
        />

        <div
          className={`relative z-[1201] ${panelWidthClass} max-w-[760px] rounded-2xl bg-blue-600 p-4 text-white`}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={onClose}
            className="absolute top-4 right-4 text-white text-3xl leading-none z-10"
            aria-label={t("events.details.more_actions.review_requests_modal.close")}
          >
            &times;
          </button>
          <h2 className="pr-8 text-center text-2xl font-bold">
            {t("events.details.more_actions.review_requests_modal.title")}
          </h2>
          <div className="mt-4">
            {loading || !requestsLoaded ? (
              <EventToolsSkeleton variant="requests" />
            ) : requests.length === 0 ? (
              <p className="text-center text-sm text-white/90">
                {t("events.details.more_actions.review_requests_modal.empty")}
              </p>
            ) : (
              <div className="max-h-[62vh] space-y-3 overflow-y-auto pr-1">
                {requests.map((requestItem, index) =>
                  renderRequestItem(requestItem, index, requests.length, "rounded-xl bg-white/10")
                )}
              </div>
            )}
          </div>

          <div className="mt-4">
            <button
              type="button"
              onClick={onClose}
              className="w-full h-11 rounded-xl font-semibold bg-white/20 text-white"
              disabled={Boolean(processingRequestId)}
            >
              {t("events.details.more_actions.review_requests_modal.close")}
            </button>
          </div>
        </div>
      </div>
    ) : (
      <AppModalShell
        open={visible}
        title={t("events.details.more_actions.review_requests_modal.title")}
        onClose={onClose}
        closeAriaLabel={t("events.details.more_actions.review_requests_modal.close")}
        zIndexClassName="z-[1200]"
        maxWidthClassName="max-w-[760px]"
        leftAction={
          typeof onBack === "function" ? (
            <EventToolsBackButton
              onClick={onBack}
              label={t("events.details.more_actions.back")}
              disabled={Boolean(processingRequestId)}
            />
          ) : null
        }
        footer={modalFooter}
      >
        <EventToolsAnimatedState
          loading={loading || !requestsLoaded}
          skeleton={<EventToolsSkeleton variant="requests" />}
        >
          {modalBody}
        </EventToolsAnimatedState>
      </AppModalShell>
    )
  );

  return (
    <>
      {content}
      {rejectionModal}
    </>
  );
};

export default ParticipantRequestsReviewModal;
